import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { CatalogueApiService } from '@angular-workspace/services';
import { mapCatalogueItemToDatasetDetailView } from '../../reference-datasets/hackathon-datasets.data';
import {
  buildEvaluationMetricsFromItem,
  getDatasetMetricsRecord,
  resolveMetricKeyLabel,
} from '../../../shared/utils/tanuh-dataset-item-payload.util';

export interface BenchmarkLibraryCard {
  id: string;
  title: string;
  description: string;
  primaryMetric: string;
  icon: 'oral' | 'breast' | 'default';
}

@Component({
  selector: 'app-benchmark-library',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './benchmark-library.component.html',
  styleUrl: './benchmark-library.component.scss',
})
export class BenchmarkLibraryComponent implements OnInit {
  datasets: BenchmarkLibraryCard[] = [];
  isLoading = true;

  constructor(private catalogueApiService: CatalogueApiService) {}

  ngOnInit(): void {
    void this.loadDatasets();
  }

  private async loadDatasets(): Promise<void> {
    try {
      const response = await this.catalogueApiService.searchAllAssets(
        '',
        ['Dataset'],
        [],
        1,
        3,
        'itemCreatedAt:desc;',
        [],
      );
      this.datasets = this.extractItems(response)
        .slice(0, 3)
        .map((item) => this.toCard(item))
        .filter((card) => !!card.id);
    } catch (error) {
      console.error('Failed to load benchmarking datasets for the landing library:', error);
      this.datasets = [];
    } finally {
      this.isLoading = false;
    }
  }

  private extractItems(response: unknown): Record<string, unknown>[] {
    const list = Array.isArray(response)
      ? response
      : this.readList(response, 'result') ??
        this.readList(response, 'results') ??
        this.readList(response, 'data') ??
        this.readList(response, 'items') ??
        [];

    return list.filter(
      (item): item is Record<string, unknown> =>
        !!item && typeof item === 'object' && !Array.isArray(item),
    );
  }

  private readList(response: unknown, key: string): unknown[] | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const value = (response as Record<string, unknown>)[key];
    if (Array.isArray(value)) {
      return value;
    }
    if (value && typeof value === 'object') {
      const nested = (value as Record<string, unknown>)['result'] ??
        (value as Record<string, unknown>)['results'] ??
        (value as Record<string, unknown>)['items'];
      if (Array.isArray(nested)) {
        return nested;
      }
    }
    return null;
  }

  private toCard(item: Record<string, unknown>): BenchmarkLibraryCard {
    const view = mapCatalogueItemToDatasetDetailView(item);
    return {
      id: view.id,
      title: view.title,
      description: view.description,
      primaryMetric: this.primaryMetricLabel(item, view.primaryMetric),
      icon: this.iconForTitle(view.title),
    };
  }

  private primaryMetricLabel(item: Record<string, unknown>, fallback: string): string {
    const primary = buildEvaluationMetricsFromItem(item).find((metric) => metric.isPrimary);
    if (primary?.label) {
      return primary.label;
    }

    const record = getDatasetMetricsRecord(item);
    const raw = String(
      item['primaryMetric'] ?? item['primary_metric'] ?? record?.['primaryMetric'] ?? fallback ?? '',
    ).trim();
    if (!raw || raw === '—') {
      return '';
    }

    return resolveMetricKeyLabel(item, raw);
  }

  private iconForTitle(title: string): BenchmarkLibraryCard['icon'] {
    const name = title.toLowerCase();
    if (name.includes('oral')) {
      return 'oral';
    }
    if (name.includes('breast')) {
      return 'breast';
    }
    return 'default';
  }
}
